import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("lm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default client;

// Public
export const fetchContent = () => client.get("/content").then((r) => r.data);

// Auth
export const login = (email, password) =>
  client.post("/auth/login", { email, password }).then((r) => r.data);
export const register = (email, password, name, recaptcha_token) =>
  client.post("/auth/register", { email, password, name, recaptcha_token }).then((r) => r.data);
export const googleLogin = (session_id) =>
  client.post("/auth/google", { session_id }).then((r) => r.data);
export const me = () => client.get("/auth/me").then((r) => r.data);

// Admin
export const adminPatchCMS = (patch) =>
  client.put("/admin/cms", { patch }).then((r) => r.data);
export const adminToggleSection = (key, enabled) =>
  client.put("/admin/sections/toggle", { key, enabled }).then((r) => r.data);
export const adminUpdatePopup = (popup) =>
  client.put("/admin/popup", popup).then((r) => r.data);
export const adminResetCMS = () => client.post("/admin/cms/reset").then((r) => r.data);
export const adminGetPlatformBalance = () => client.get("/admin/platform/balance").then((r) => r.data);
export const adminGetPlatformTypes = () => client.get("/admin/platform/packageTypes").then((r) => r.data);
export const adminGetPlatformPackages = () => client.get("/admin/platform/packages").then((r) => r.data);
export const adminGetOrders = () => client.get("/admin/orders").then((r) => r.data);
export const adminGetUsers = () => client.get("/admin/users").then((r) => r.data);
export const adminCreateUser = (p) => client.post("/admin/users", p).then((r) => r.data);
export const adminUpdateUser = (id, p) => client.put(`/admin/users/${id}`, p).then((r) => r.data);
export const adminDeleteUser = (id) => client.delete(`/admin/users/${id}`).then((r) => r.data);
export const adminGetPlatformStackUsers = () => client.get("/admin/platform/stack-users").then((r) => r.data);
export const adminSyncUsersFromPlatform = () => client.post("/admin/users/sync-platform").then((r) => r.data);

// Public config (non-secret keys)
export const getPublicConfig = () => client.get("/public/config").then((r) => r.data);

// Tawk.to HMAC auth
export const chatAuth = () => client.post("/chat/auth").then((r) => r.data);

// Hosting: Unlink (suspend on platform + remove from DB) vs Delete (cascade=true wipes on platform too)
export const adminUnlinkHosting = (id) => client.post(`/admin/hosting/${id}/unlink`).then((r) => r.data);
export const adminRelinkHosting = (id) => client.post(`/admin/hosting/${id}/relink`).then((r) => r.data);
export const adminDeleteHostingCascade = (id) =>
  client.delete(`/admin/hosting/${id}?cascade=true`).then((r) => r.data);

// Domain availability
export const checkDomain = (name) => client.get(`/domain/check`, { params: { name } }).then((r) => r.data);

// Hosting management — SSO + Welcome email
export const adminHostingSso = (id) => client.post(`/admin/hosting/${id}/sso`).then((r) => r.data);
export const customerHostingSso = (id) => client.post(`/customer/hosting/${id}/sso`).then((r) => r.data);
export const customerSendWelcome = (id) => client.post(`/customer/hosting/${id}/send-welcome`).then((r) => r.data);
export const customerGetCredentials = (id) =>
  client.get(`/customer/hosting/${id}/credentials`).then((r) => r.data);
export const customerChangeStackPassword = (id, newPassword) =>
  client
    .post(`/customer/hosting/${id}/change-stack-password`, { hosting_id: id, new_password: newPassword })
    .then((r) => r.data);

export const createCheckoutOrder = (payload) =>
  client.post("/checkout/create-order", payload).then((r) => r.data);
export const verifyCheckout = (payload) =>
  client.post("/checkout/verify", payload).then((r) => r.data);

// WHMCS-like management
export const adminGetDashboard = () => client.get("/admin/dashboard").then((r) => r.data);

export const adminListCustomers = () => client.get("/admin/customers").then((r) => r.data);
export const adminCreateCustomer = (p) => client.post("/admin/customers", p).then((r) => r.data);
export const adminGetCustomer = (id) => client.get(`/admin/customers/${id}`).then((r) => r.data);
export const adminUpdateCustomer = (id, p) => client.put(`/admin/customers/${id}`, p).then((r) => r.data);
export const adminDeleteCustomer = (id) => client.delete(`/admin/customers/${id}`).then((r) => r.data);

export const adminListHosting = () => client.get("/admin/hosting").then((r) => r.data);
export const adminCreateHosting = (p) => client.post("/admin/hosting", p).then((r) => r.data);
export const adminUpdateHosting = (id, p) => client.put(`/admin/hosting/${id}`, p).then((r) => r.data);
export const adminRefreshStackUser = (id) =>
  client.post(`/admin/hosting/${id}/refresh-stack-user`).then((r) => r.data);
export const adminSyncStackUser = (id) =>
  client.post(`/admin/hosting/${id}/sync-stackuser`).then((r) => r.data);
export const adminBulkSyncStackUsers = () =>
  client.post(`/admin/hosting/bulk-sync-stackusers`).then((r) => r.data);
export const adminBulkCreateSupportUsers = () =>
  client.post(`/admin/hosting/bulk-create-support-users`).then((r) => r.data);

// Billing Automation (Dunning)
export const adminGetDunningSettings = () =>
  client.get(`/admin/dunning/settings`).then((r) => r.data);
export const adminUpdateDunningSettings = (payload) =>
  client.put(`/admin/dunning/settings`, payload).then((r) => r.data);
export const adminRunDunningNow = (dryRun = true) =>
  client.post(`/admin/dunning/run-now?dry_run=${dryRun}`).then((r) => r.data);
export const adminGetSupportCredentials = (id) =>
  client.get(`/admin/hosting/${id}/support-credentials`).then((r) => r.data);
export const adminSetSupportCredentials = (id, payload) =>
  client.put(`/admin/hosting/${id}/support-credentials`, payload).then((r) => r.data);
export const adminUpdateHostingShare = (id, emails) =>
  client.put(`/admin/hosting/${id}/share`, { shared_with_emails: emails }).then((r) => r.data);
export const adminConsolidateHosting = (payload) =>
  client.post("/admin/hosting/consolidate", payload).then((r) => r.data);
export const adminSuspendHosting = (id) => client.post(`/admin/hosting/${id}/suspend`).then((r) => r.data);
export const adminUnsuspendHosting = (id) => client.post(`/admin/hosting/${id}/unsuspend`).then((r) => r.data);
export const adminRenewHosting = (p) => client.post("/admin/hosting/renew", p).then((r) => r.data);
export const adminDeleteHosting = (id) => client.delete(`/admin/hosting/${id}`).then((r) => r.data);

export const adminListInvoices = () => client.get("/admin/invoices").then((r) => r.data);
export const adminCreateInvoice = (p) => client.post("/admin/invoices", p).then((r) => r.data);
export const adminUpdateInvoice = (id, p) => client.put(`/admin/invoices/${id}`, p).then((r) => r.data);
export const adminDeleteInvoice = (id) => client.delete(`/admin/invoices/${id}`).then((r) => r.data);

// Uploads
export const adminUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Platform sync & control panel
export const adminPlatformSync = () => client.post("/admin/platform/sync").then((r) => r.data);
export const adminHostingDuplicates = () => client.get("/admin/hosting/duplicates").then((r) => r.data);
export const adminMergeDuplicates = (keeperId, dropIds) =>
  client.post("/admin/hosting/merge-duplicates", { keeper_id: keeperId, drop_ids: dropIds }).then((r) => r.data);
export const adminHostingControlPanel = (id) =>
  client.get(`/admin/hosting/${id}/control-panel`).then((r) => r.data);
export const adminChangeStackPassword = (id, newPassword) =>
  client
    .post(`/admin/hosting/${id}/change-stack-password`, { hosting_id: id, new_password: newPassword })
    .then((r) => r.data);
export const adminSendWelcome = (hosting_id, to_address) =>
  client.post("/admin/hosting/send-welcome", { hosting_id, to_address }).then((r) => r.data);

// Pages (CMS)
export const listPages = () => client.get("/pages").then((r) => r.data);
export const getPage = (slug) => client.get(`/pages/${slug}`).then((r) => r.data);
export const adminListPages = () => client.get("/admin/pages").then((r) => r.data);
export const adminCreatePage = (p) => client.post("/admin/pages", p).then((r) => r.data);
export const adminGetPage = (id) => client.get(`/admin/pages/${id}`).then((r) => r.data);
export const adminUpdatePage = (id, p) => client.put(`/admin/pages/${id}`, p).then((r) => r.data);
export const adminDeletePage = (id) => client.delete(`/admin/pages/${id}`).then((r) => r.data);

// Customer portal (auth required as user)
export const customerListHosting = () => client.get("/customer/hosting").then((r) => r.data);
export const customerListInvoices = () => client.get("/customer/invoices").then((r) => r.data);
export const customerControlPanel = (id) =>
  client.get(`/customer/control-panel/${id}`).then((r) => r.data);

// Email / password-setup
export const adminSendPasswordSetup = (userId) =>
  client.post(`/admin/users/${userId}/send-password-setup`).then((r) => r.data);
export const adminEmailCredentials = (hostingId, toAddress) =>
  client
    .post(`/admin/hosting/${hostingId}/email-credentials`, { hosting_id: hostingId, to_address: toAddress || null })
    .then((r) => r.data);
export const verifyPasswordSetupToken = (token) =>
  client.post(`/auth/password-setup/verify`, { token }).then((r) => r.data);
export const completePasswordSetup = (token, newPassword) =>
  client.post(`/auth/password-setup/complete`, { token, new_password: newPassword }).then((r) => r.data);
export const forgotPassword = (email) =>
  client.post(`/auth/forgot-password`, { email }).then((r) => r.data);

// Invoice / tax settings
export const adminGetInvoiceSettings = () => client.get("/admin/settings/invoice").then((r) => r.data);
export const adminUpdateInvoiceSettings = (p) => client.put("/admin/settings/invoice", p).then((r) => r.data);
export const getPublicTaxConfig = () => client.get("/public/tax-config").then((r) => r.data);

// Customer renewal
export const customerRenewOptions = (id) =>
  client.get(`/customer/hosting/${id}/renew-options`).then((r) => r.data);
export const customerRequestRenewal = (id, months) =>
  client.post(`/customer/hosting/${id}/request-renewal`, { months }).then((r) => r.data);

// Maintenance + audit
export const adminRunExpiryCheck = (graceDays = 0) =>
  client.post(`/admin/hosting/run-expiry-check?grace_days=${graceDays}`).then((r) => r.data);
export const adminGetAuditLog = (limit = 200) =>
  client.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data);
export const adminRunRenewalNudges = () =>
  client.post(`/admin/hosting/run-renewal-nudges`).then((r) => r.data);

// Git / deploy / backup
export const adminGitStatus = () => client.get("/admin/git/status").then((r) => r.data);
export const adminGitPull = (restart = true) =>
  client.post(`/admin/git/pull?restart=${restart ? "true" : "false"}`).then((r) => r.data);
export const adminGitBackupDb = () => client.post(`/admin/git/backup-db`).then((r) => r.data);
export const adminGitListBackups = (limit = 50) =>
  client.get(`/admin/git/backups?limit=${limit}`).then((r) => r.data);

// Customer self-serve Razorpay renewal
export const customerRenewCreateOrder = (id, months) =>
  client.post(`/customer/hosting/${id}/renew-create-order`, { months }).then((r) => r.data);
export const customerRenewVerify = (id, payload) =>
  client.post(`/customer/hosting/${id}/renew-verify`, payload).then((r) => r.data);

// Platform sync with optional auto-merge
export const adminPlatformSyncWithMerge = (autoMerge = false) =>
  client.post(`/admin/platform/sync?auto_merge=${autoMerge ? "true" : "false"}`).then((r) => r.data);

// 20i Stack-user Audit
export const adminStackUserAudit = () => client.get("/admin/stackuser-audit").then((r) => r.data);
export const adminStackUserLinkAsSupport = (payload) =>
  client.post("/admin/stackuser-audit/link-as-support", payload).then((r) => r.data);
export const adminStackUserDelete = (stack_user_ref) =>
  client.post("/admin/stackuser-audit/delete", { stack_user_ref }).then((r) => r.data);

// Locked renewal price (per hosting + bulk per customer)
export const adminSetLockedRenewalPrice = (hostingId, price) =>
  client.put(`/admin/hosting/${hostingId}/locked-renewal-price`, { locked_renewal_price: price }).then((r) => r.data);
export const adminBulkLockRenewalForCustomer = (customer_id, price) =>
  client.post(`/admin/hosting/bulk-lock-renewal`, { customer_id, locked_renewal_price: price }).then((r) => r.data);

// Payment Links (repeat-purchase / pay-now flow)
export const adminCreatePaymentLink = (payload) =>
  client.post("/admin/payment-links/create", payload).then((r) => r.data);
export const adminListPaymentLinks = (status) =>
  client.get(`/admin/payment-links${status ? `?status=${status}` : ""}`).then((r) => r.data);
export const adminResendPaymentLink = (token) =>
  client.post(`/admin/payment-links/${token}/resend`).then((r) => r.data);
export const adminCancelPaymentLink = (token, reason) =>
  client.post(`/admin/payment-links/${token}/cancel`, { reason }).then((r) => r.data);
export const getPaymentLink = (token) =>
  client.get(`/payment-links/${token}`).then((r) => r.data);
export const verifyPaymentLink = (token, payload) =>
  client.post(`/payment-links/${token}/verify`, payload).then((r) => r.data);
export const customerPendingPayLinks = () =>
  client.get("/customer/payment-links").then((r) => r.data);

// Pay-Now flow for UNPAID invoices (e.g. self-requested renewals)
export const customerInvoicePayCreateOrder = (invoiceId) =>
  client.post(`/customer/invoices/${invoiceId}/pay-create-order`).then((r) => r.data);
export const customerInvoicePayVerify = (invoiceId, payload) =>
  client.post(`/customer/invoices/${invoiceId}/pay-verify`, payload).then((r) => r.data);

// Retry creating the 20i Support StackCP user for a single hosting
export const adminRetrySupportUser = (hostingId) =>
  client.post(`/admin/hosting/${hostingId}/retry-support-user`).then((r) => r.data);

// Try-landing CMS
export const getTryLanding = () => client.get("/try-landing").then((r) => r.data);
export const adminUpdateTryLanding = (payload) =>
  client.put("/admin/try-landing", payload).then((r) => r.data);
